import { useMemo } from "react";
import { Space } from "antd";
import { Toggle } from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { Animations } from "../../controls/Animations";
import { CommitSlider } from "../../controls/CommitSlider";
import { PanelSection } from "../PanelSection";
import {
  getCurrentTextItem,
  getCurrentTextAction,
} from "../../../helpers/textHelpers";
import {
  Avatar,
  CustomText,
  ITEM_TYPES,
  MediaItem,
  Shape,
  TableDividerStyle,
  TableTextStyle,
} from "../../../types";

export const AnimationsTab = () => {
  const t = useTranslate();

  const {
    currentItem,
    presenter,
    setPresenter,
    mainImage,
    images,
    videos,
    updateMainImage,
    updateImage,
    updateVideo,
    shapes,
    slideLayoutShapes,
    updateShape,
    titleStyle,
    textStyle,
    customTexts,
    parameteredTexts,
    figure,
    setTitleStyle,
    setTextStyle,
    updateCustomText,
    updateParameteredText,
    updateFigure,
    table,
    updateTable,
    updateTableTextStyle,
    updateTableDividerStyle,
  } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    presenter: store.templateState.presenter,
    setPresenter: store.setPresenter,
    mainImage: store.templateState.mainImage,
    images: store.templateState.images,
    videos: store.templateState.videos,
    updateMainImage: store.updateMainImage,
    updateImage: store.updateImage,
    updateVideo: store.updateVideo,
    shapes: store.templateState.shapes,
    slideLayoutShapes: store.templateState.slideLayoutShapes,
    updateShape: store.updateShape,
    titleStyle: store.templateState.titleStyle,
    textStyle: store.templateState.textStyle,
    customTexts: store.templateState.customTexts,
    parameteredTexts: store.templateState.parameteredTexts,
    figure: store.templateState.figure,
    setTitleStyle: store.setTitleStyle,
    setTextStyle: store.setTextStyle,
    updateCustomText: store.updateCustomText,
    updateParameteredText: store.updateParameteredText,
    updateFigure: store.updateFigure,
    table: store.templateState.table,
    updateTable: store.updateTable,
    updateTableTextStyle: store.updateTableTextStyle,
    updateTableDividerStyle: store.updateTableDividerStyle,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const itemType = currentItem?.type;

  // --- Text items ---
  const textItem = getCurrentTextItem({
    currentItem,
    titleStyle,
    textStyle,
    customTexts,
    parameteredTexts,
    figure,
  });
  const textSetAction = getCurrentTextAction({
    currentItem,
    setTitleStyle,
    setTextStyle,
    updateCustomText,
    updateParameteredText,
    updateFigure,
    customTexts,
    parameteredTexts,
    figure,
  });

  // --- Media items ---
  const currentMainImage =
    mainImage?.id === currentItem?.id ? mainImage : undefined;
  const currentImage = images.find((img) => img.id === currentItem?.id);
  const currentVideo = videos.find((vid) => vid.id === currentItem?.id);
  const pickedMediaItem = currentMainImage ?? currentImage ?? currentVideo;

  const pickedMediaAction = useMemo(() => {
    if (currentMainImage) return updateMainImage;
    if (currentImage) return updateImage;
    return updateVideo;
  }, [
    updateMainImage,
    updateImage,
    updateVideo,
    currentMainImage,
    currentImage,
  ]);

  // --- Shape items ---
  const currentShape =
    shapes.find((s) => s.id === currentItem?.id) ??
    slideLayoutShapes?.find((s) => s.id === currentItem?.id);

  // --- Table handlers ---
  const handleTableTextStyleChange = (style: Partial<TableTextStyle>) => {
    updateTableTextStyle(style);
    addTemplateUpdate();
  };

  const handleTableDividerStyleChange = (style: Partial<TableDividerStyle>) => {
    updateTableDividerStyle(style);
    addTemplateUpdate();
  };

  // --- Render based on selected item type ---
  if (itemType === ITEM_TYPES.Table && table) {
    return (
      <>
        <PanelSection
          title={t("components.templates.settings.animationsTab.tabTitle")}
          subtitle={t(
            "components.templates.settings.animationsTab.description"
          )}
          primary
          noBorder
        />
        <PanelSection
          title={t("components.templates.settings.tableTab.rowDelay")}
          noBorder
        >
          <CommitSlider
            value={table.animationRowDelay ?? 0}
            onCommit={(value) => {
              updateTable({ animationRowDelay: value });
              addTemplateUpdate();
            }}
            min={0}
            max={2}
            step={0.1}
            tooltip={{ formatter: (val) => `${val}s` }}
          />
        </PanelSection>

        <PanelSection
          title={t("components.templates.settings.tableTab.textAnimation")}
          noBorder
        >
          <Animations
            pickedItem={table.textStyle}
            onChange={(value) => {
              handleTableTextStyleChange(value as Partial<TableTextStyle>);
            }}
          />
        </PanelSection>

        {table.showDividers && (
          <PanelSection
            title={t("components.templates.settings.tableTab.dividerAnimation")}
            noBorder
          >
            <Animations
              pickedItem={table.dividerStyle}
              onChange={(value) => {
                handleTableDividerStyleChange(
                  value as Partial<TableDividerStyle>
                );
              }}
            />
          </PanelSection>
        )}
      </>
    );
  }

  if (itemType === ITEM_TYPES.Presenter) {
    return (
      <PanelSection
        title={t("components.templates.settings.animationsTab.tabTitle")}
        subtitle={t("components.templates.settings.animationsTab.description")}
        primary
        noBorder
      >
        <Animations
          pickedItem={presenter}
          onChange={(value) => {
            setPresenter(value as Partial<Avatar>);
            addTemplateUpdate();
          }}
        />
      </PanelSection>
    );
  }

  if (
    (itemType === ITEM_TYPES.Image || itemType === ITEM_TYPES.Video) &&
    pickedMediaItem
  ) {
    return (
      <PanelSection
        title={t("components.templates.settings.animationsTab.tabTitle")}
        subtitle={t("components.templates.settings.animationsTab.description")}
        primary
        noBorder
      >
        <Animations
          pickedItem={pickedMediaItem}
          onChange={(value) => {
            pickedMediaAction(value as Partial<MediaItem>);
            addTemplateUpdate();
          }}
        />
      </PanelSection>
    );
  }

  if (itemType === ITEM_TYPES.Shape && currentShape) {
    return (
      <PanelSection
        title={t("components.templates.settings.animationsTab.tabTitle")}
        subtitle={t("components.templates.settings.animationsTab.description")}
        primary
        noBorder
      >
        <Animations
          pickedItem={currentShape}
          onChange={(value) => {
            updateShape(value as Partial<Shape>);
            addTemplateUpdate();
          }}
        />
      </PanelSection>
    );
  }

  if (
    (itemType === ITEM_TYPES.Title ||
      itemType === ITEM_TYPES.Subtitles ||
      itemType === ITEM_TYPES.CustomText ||
      itemType === ITEM_TYPES.ParameteredText) &&
    textItem &&
    textSetAction
  ) {
    const pickedParameteredText = parameteredTexts?.find(
      (text) => text.id === currentItem?.id
    );

    return (
      <>
        <PanelSection
          title={t("components.templates.settings.animationsTab.tabTitle")}
          subtitle={t(
            "components.templates.settings.animationsTab.description"
          )}
          primary
          noBorder
        >
          <Animations
            pickedItem={textItem}
            onChange={(value) => {
              textSetAction(value as Partial<CustomText>);
              addTemplateUpdate();
            }}
          />
        </PanelSection>
        {pickedParameteredText && (
          <PanelSection
            title={t(
              "components.templates.settings.animationsTab.wordAnimation"
            )}
            noBorder
          >
            <Space align="center">
              <Toggle
                checked={pickedParameteredText.animated}
                onChange={(checked) => {
                  updateParameteredText({
                    animated: checked,
                    id: pickedParameteredText.id,
                  });
                  addTemplateUpdate();
                }}
              />
            </Space>
          </PanelSection>
        )}
      </>
    );
  }

  return (
    <PanelSection
      title={t("components.templates.settings.animationsTab.tabTitle")}
      subtitle={t("components.templates.settings.animationsTab.noItem")}
      primary
      noBorder
    />
  );
};
